import { ErrorBoundary } from "@ant-design/pro-components";
import { Outlet, useSearchParams } from "react-router-dom";
import { useState, useEffect } from "react";
import InviteAcceptModal, { PENDING_INVITE_TOKEN_KEY } from "../../components/InviteAcceptModal";
import MealApprovalModal from "../../components/MealApprovalModal";
import { useEnsureSessionReady } from "../../hooks/useEnsureSessionReady";

/** Stashed from URL when user opens a meal-approval link before logging in. */
export const PENDING_MEAL_APPROVE_TOKEN_KEY = "glucera_pending_meal_approve_token";

const GluceraLayout = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Family invite state
  const [inviteToken, setInviteToken] = useState<string | null>(null);
  const [showInviteModal, setShowInviteModal] = useState(false);

  // Meal approval from URL token only (home-data approvals live on HomePage)
  const [mealApprovalToken, setMealApprovalToken] = useState<string | null>(null);
  const [showMealApprovalModal, setShowMealApprovalModal] = useState(false);

  const { canUseAuthenticatedApis } = useEnsureSessionReady();

  useEffect(() => {
    const inviteTokenParam = searchParams.get("family_invite_token");
    if (inviteTokenParam) {
      if (canUseAuthenticatedApis) {
        setInviteToken(inviteTokenParam);
        setShowInviteModal(true);
      } else {
        try {
          localStorage.setItem(PENDING_INVITE_TOKEN_KEY, inviteTokenParam);
        } catch {
          // ignore
        }
      }
      searchParams.delete("family_invite_token");
      setSearchParams(searchParams, { replace: true });
    }

    const mealTokenParam = searchParams.get("meal_approve_token");
    if (mealTokenParam) {
      if (canUseAuthenticatedApis) {
        setMealApprovalToken(mealTokenParam);
        setShowMealApprovalModal(true);
      } else {
        try {
          sessionStorage.setItem(PENDING_MEAL_APPROVE_TOKEN_KEY, mealTokenParam);
        } catch {
          // ignore
        }
      }
      searchParams.delete("meal_approve_token");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, canUseAuthenticatedApis]);

  useEffect(() => {
    if (!canUseAuthenticatedApis) return;
    let pending: string | null = null;
    try {
      pending = localStorage.getItem(PENDING_INVITE_TOKEN_KEY);
    } catch {
      pending = null;
    }
    if (pending) {
      try {
        localStorage.removeItem(PENDING_INVITE_TOKEN_KEY);
      } catch {
        // ignore
      }
      setInviteToken(pending);
      setShowInviteModal(true);
    }
  }, [canUseAuthenticatedApis]);

  useEffect(() => {
    if (!canUseAuthenticatedApis) return;
    let pending: string | null = null;
    try {
      pending = sessionStorage.getItem(PENDING_MEAL_APPROVE_TOKEN_KEY);
    } catch {
      pending = null;
    }
    if (pending) {
      try {
        sessionStorage.removeItem(PENDING_MEAL_APPROVE_TOKEN_KEY);
      } catch {
        // ignore
      }
      setMealApprovalToken(pending);
      setShowMealApprovalModal(true);
    }
  }, [canUseAuthenticatedApis]);

  const handleInviteModalClose = () => {
    setShowInviteModal(false);
    setInviteToken(null);
  };

  const handleMealApprovalModalClose = () => {
    setShowMealApprovalModal(false);
    setMealApprovalToken(null);
  };

  return (
    <div className="min-h-screen bg-background">
      {canUseAuthenticatedApis ? (
        <>
          <InviteAcceptModal
            inviteToken={inviteToken}
            isOpen={showInviteModal}
            onClose={handleInviteModalClose}
          />
          <MealApprovalModal
            approvalToken={mealApprovalToken}
            isOpen={showMealApprovalModal}
            onClose={handleMealApprovalModalClose}
          />
        </>
      ) : null}
      <ErrorBoundary>
        <Outlet />
      </ErrorBoundary>
    </div>
  );
};

export default GluceraLayout;
